import React, { useEffect, useMemo, useState } from "react";
import { getAssetUrl } from "@/lib/getAssetUrl";
import type { AssetSrc } from "@/assets";
import { toAssetSrc } from "@/assets";

type CustomImageProps = Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  "src"
> & {
  src: string | File | AssetSrc;
  alt: string;
  priority?: boolean;
  fill?: boolean;
};

const CustomImage: React.FC<CustomImageProps> = ({
  src,
  alt,
  loading,
  priority,
  fill,
  className,
  style,
  ...imageProps
}) => {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);

  useEffect(() => {
    if (typeof src === "object" && src instanceof File) {
      const nextObjectUrl = URL.createObjectURL(src);
      setObjectUrl(nextObjectUrl);

      return () => {
        URL.revokeObjectURL(nextObjectUrl);
      };
    }

    setObjectUrl(null);
    return undefined;
  }, [src]);

  const resolvedSrc = useMemo(() => {
    if (typeof src === "object" && src instanceof File) {
      return objectUrl ?? "";
    }

    if (typeof src === "object" && src !== null && "src" in src) {
      return String((src as { src: string }).src);
    }

    return getAssetUrl(toAssetSrc(src as AssetSrc));
  }, [objectUrl, src]);

  const mergedClassName = fill
    ? [className, "absolute inset-0 h-full w-full object-cover"]
        .filter(Boolean)
        .join(" ")
    : className;

  const mergedStyle = fill
    ? { ...style, position: "relative" as const }
    : style;

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      className={mergedClassName}
      style={mergedStyle}
      loading={loading ?? (priority ? "eager" : "lazy")}
      {...imageProps}
    />
  );
};

export default CustomImage;
